/**
 * Brand overrides for the ApartmentSync (Repli360) floor plan and interactive
 * map widgets. The vendor stylesheet is injected inside the widget, after the
 * site's own styles, and paints its configured accent with `!important`, so
 * every rule below repeats the vendor selector behind `html body` to win on
 * specificity. Popups and modals are appended straight to <body>, so they are
 * matched by their own rrac-prefixed classes rather than the widget root.
 */

import { vendorAccentSelectors as vendor } from './apartment-sync-vendor-selectors';

const SLATE = 'var(--vibe-color-primary)';
const SLATE_HOVER = '#4A5355';
const TRIM_WHITE = '#E8E8E4';
const INK = '#000000';
const WHITE = '#FFFFFF';
const MUTED_INK = '#595959';

const scoped = (selectors: string[]) => selectors.map((s) => `html body ${s}`).join(',\n  ');

const vendorAccentCss = `
  ${scoped(vendor.background)} {
    background: ${SLATE} !important;
    color: ${WHITE} !important;
    border-color: ${SLATE} !important;
  }
  ${scoped(vendor.backgroundHover)} {
    background: ${SLATE_HOVER} !important;
    color: ${WHITE} !important;
    border-color: ${SLATE_HOVER} !important;
  }
  ${scoped(vendor.color)} {
    color: ${SLATE} !important;
  }
  ${scoped(vendor.border)} {
    border-color: ${SLATE} !important;
  }
  ${scoped(vendor.underline)} {
    border-bottom-color: ${SLATE} !important;
    box-shadow: inset 0 -1px 0 0 ${SLATE} !important;
  }
  ${scoped(vendor.fill)} {
    fill: ${SLATE} !important;
  }
  ${scoped(vendor.scrollbar)} {
    scrollbar-color: ${SLATE} ${WHITE} !important;
  }
`;

export const apartmentSyncCss = `
  ${vendorAccentCss}

  html body :is(.rrac_script, .rrac_interactive_leasing_view) {
    --color_1: ${SLATE};
  }

  /* Tabs: inactive Trim White / black, active Slate / white, hover darker Slate. */
  html body .rrac_tabSection .rrac-tabs li a,
  html body .rrac_tabSection.rracListView_4 .main-wrap .nav-sec ul li a,
  html body .phase-rrac-tabs .phase_rrac_tabs_id a,
  html body .rrac_Floorplan_Gallery_Content .rrac_gallery_nav li a {
    background: ${TRIM_WHITE} !important;
    color: ${INK} !important;
  }
  html body .rrac_tabSection .rrac-tabs li.active a,
  html body .rrac_tabSection.rracListView_4 .main-wrap .nav-sec ul li.active a,
  html body .phase-rrac-tabs .phase_rrac_tabs_id.active a,
  html body .rrac_Floorplan_Gallery_Content .rrac_gallery_nav li.active a {
    background: ${SLATE} !important;
    color: ${WHITE} !important;
  }
  html body .rrac_tabSection .rrac-tabs li a:hover,
  html body .rrac_tabSection .rrac-tabs li.active a:hover,
  html body .rrac_tabSection.rracListView_4 .main-wrap .nav-sec ul li a:hover,
  html body .rrac_tabSection.rracListView_4 .main-wrap .nav-sec ul li.active a:hover,
  html body .phase-rrac-tabs .phase_rrac_tabs_id a:hover,
  html body .rrac_Floorplan_Gallery_Content .rrac_gallery_nav li a:hover {
    background: ${SLATE_HOVER} !important;
    color: ${WHITE} !important;
  }
  html body .rracSentosa .rrac-tabs li.active a:after,
  html body .rrac_Floorplan_Gallery_Content .rrac_gallery_nav li.active a:after {
    color: ${SLATE} !important;
  }
  html body .rrac_tabSection.rracListView_4 .rrac_extra_floorplan_tab_menu ul li .rrac_extra_floorplan_tabs.active {
    color: ${SLATE} !important;
    border-color: ${SLATE} !important;
  }

  /* Card buttons (View Units, Schedule a Tour) and "Available Now" badges. */
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec ul li a,
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec ul li:first-child a,
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec ul li:last-child a,
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec h4.special_avail a,
  html body .rrac_tabSection .special_avail a,
  html body .temSix_field_tabs .page-tabs .tab-content .floorPlanGrid .floorPlanItem .floorPlanImage h4.special_avail,
  html body .rrac_tabSection.rracListView_4 .main-wrap .navfilterex #rrac_filterContent ul li button[type="button"],
  html body .rrac_tabSection.rracListView_4 .srchCriBtnWrapper .srchCriBtn {
    background: ${SLATE} !important;
    color: ${WHITE} !important;
    border: 1px solid ${SLATE} !important;
  }
  html body .temSix_field_tabs .page-tabs .tab-content .floorPlanGrid .floorPlanItem .floorPlanImage h4.special_avail::after {
    border-color: ${SLATE} !important;
    border-left-color: transparent !important;
  }
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec ul li a:hover,
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec ul li:first-child a:hover,
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec ul li:last-child a:hover,
  html body .rrac_tabSection.rracListView_4 .main-wrap .navfilterex #rrac_filterContent ul li button[type="button"]:hover,
  html body .rrac_tabSection.rracListView_4 .srchCriBtnWrapper .srchCriBtn:hover,
  html body .rrac_tabSection .loadmoreContainer a:hover {
    background: ${SLATE_HOVER} !important;
    color: ${WHITE} !important;
    border-color: ${SLATE_HOVER} !important;
  }

  /* Price, "Starting", "Available Now" and count text: Slate on the white /
     #F2F2F2 card. */
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec .pricing,
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec.unit_price_lease .pricing,
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec.unit_price_lease .rrac-price,
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec.unit_price_lease .fp-fee-disclamer strong,
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec .avail_msg,
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec h3 span,
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec .fp-specials-section .fp-special-validity,
  html body .rrac_tabSection.rracListView_4 .main-wrap .filter-sec h2.available-floor-text,
  html body .rrac_tabSection.rracListView_4 .main-wrap .filter-sec h2.available-floor-text span,
  html body .rrac_floorplanDetails .fp_details_plfull .right_part span {
    color: ${SLATE} !important;
  }
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec h2:after {
    background: ${SLATE} !important;
  }
  html body .rrac_tabSection .pro-sec .left-sec .counter_secc:before {
    border-top-color: ${SLATE} !important;
  }

  /* Filter button: Slate outline and bars, darker label. */
  html body .rrac_tabSection.rracListView_4 .main-wrap .navfilterex .filterbtn.rrac_filterBtn {
    border: 1px solid ${SLATE} !important;
  }
  html body .rrac_tabSection.rracListView_4 .main-wrap .navfilterex .filterbtn.rrac_filterBtn .icon-bar {
    background: ${SLATE} !important;
  }
  html body .rrac_tabSection.rracListView_4 .main-wrap .navfilterex .filterbtn.rrac_filterBtn small,
  html body .rrac_tabSection.rracListView_4 .pro-sec .right-sec h4 {
    color: ${MUTED_INK} !important;
  }

  /* Range sliders and the pulsing "spotlight" filter. */
  html body #rrac_filterContent .noUi-handle,
  html body .rrac_tabSection .top-level-filter .noUi-handle,
  html body .rrac_tabSection.rracListView_4 .main-wrap .navfilterex #rrac_filterContent ul li .noUi-horizontal .noUi-connect,
  html body .rrac_tabSection.rracListView_4 .main-wrap .top-level-filter .noUi-horizontal .noUi-connect {
    background: ${SLATE} !important;
  }
  html body .rrac_tabSection .spot-light {
    animation: none !important;
  }

  /* Gallery, unit-detail popups and the date picker. */
  html body #rrac_Floorplan_Gallery .rrac_Floorplan_Gallery_Content .rrac_gallery_tab_content .owl-carousel .owl-nav span,
  html body .rrac_floorplanDetails .owl-nav span {
    color: ${SLATE} !important;
  }
  html body #rrac_Floorplan_Gallery .rrac_galleryClose {
    color: ${SLATE} !important;
    border-color: ${SLATE} !important;
  }
  html body #rrac_apartment_details .rrac_listAvailableUnit table tr td a:hover,
  html body #rrac_datepickerView .rrac_datepicker_content .leaseNowButton a:hover,
  html body .rrac_floorplanDetails .fp_details_plfull .right_part a.btn:hover,
  html body .apartment_sync_grid_view .blok .bot li:first-child a:hover,
  html body .rrac_showAllUnit .emptyUnitMessage a:hover {
    background: ${SLATE_HOVER} !important;
    color: ${WHITE} !important;
    border-color: ${SLATE_HOVER} !important;
  }
  html body .daterangepicker td.active,
  html body .daterangepicker td.active:hover {
    background: ${SLATE} !important;
    color: ${WHITE} !important;
    border-color: ${SLATE} !important;
  }
  html body :is(.rrac-popup, .rrac_modal_container) .popovers-area {
    scrollbar-color: ${SLATE} ${WHITE} !important;
  }

  /* Keyboard focus: the vendor strips outlines from its filter button. */
  html body :is(.rrac_script, .rrac_interactive_leasing_view, .rrac-popup, .rrac_modal_container) :is(a, button, input, select):focus-visible,
  html body .rrac_tabSection.rracListView_4 .main-wrap .navfilterex .filterbtn.rrac_filterBtn:focus-visible {
    outline: 2px solid ${INK} !important;
    outline-offset: 2px !important;
  }
`;
